import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageSquare, ArrowRight, Camera, Star, Users, CloudLightning, Flame } from "lucide-react";
import api from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/ui/States";
import { URGENCY_COLOR, URGENCY_LABEL, formatPhone, timeAgo } from "@/lib/leadFormat";

const QUICK_ACTIONS = [
    { to: "/admin/leads", label: "All Leads", icon: Users, testId: "today-qa-leads" },
    { to: "/admin/photos", label: "Photo Manager", icon: Camera, testId: "today-qa-photos" },
    { to: "/admin/reviews", label: "Reviews", icon: Star, testId: "today-qa-reviews" },
    { to: "/admin/storm", label: "Storm Mode", icon: CloudLightning, testId: "today-qa-storm" },
];

export default function PocketDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => api.get("/admin/dashboard/today").then((r) => setData(r.data)).finally(() => setLoading(false));

    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, []);

    if (loading) return <LoadingState />;
    if (!data) return null;

    const dateLabel = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    const asOf = new Date(data.as_of).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

    return (
        <div data-testid="pocket-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-2">
                <h1 className="m-0">Today</h1>
                <p className="text-sm text-[var(--color-text-muted)] m-0">
                    {dateLabel} · as of {asOf} · refreshes every minute
                </p>
            </div>

            {data.hot_alerts > 0 ? (
                <Link to="/admin/chats" className="today-hot-banner mt-4" data-testid="today-hot-alerts-banner">
                    <Flame size={18} className="shrink-0" />
                    <span>
                        <strong>{data.hot_alerts} hot chat {data.hot_alerts === 1 ? "lead" : "leads"}</strong> waiting — a visitor shared contact info with a real repair need.
                    </span>
                    <span className="ml-auto font-bold shrink-0">Open Chats →</span>
                </Link>
            ) : null}

            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mt-5 mb-7">
                <Stat to="/admin/leads" label="Leads today" value={data.today_leads} testId="pd-today-leads" />
                <Stat to="/admin/leads" label="Storm queue" value={data.storm_queue} warning={data.storm_queue > 0} testId="pd-storm-queue" />
                <Stat to="/admin/chats" label="Chats today" value={data.chats_today ?? 0} testId="pd-chats-today" />
                <Stat to="/admin/photos" label="Photos to review" value={data.photos_awaiting_review} warning={data.photos_awaiting_review > 10} testId="pd-photos-awaiting" />
                <Stat to="/admin/reviews" label="Reviews pending" value={data.pending_reviews} testId="pd-pending-reviews" />
            </div>

            <div className="grid gap-6 lg:grid-cols-2 mb-7">
                <section data-testid="today-new-leads">
                    <SectionHead title="Needs first contact" count={data.new_leads.length} />
                    {data.new_leads.length === 0 ? (
                        <EmptyState headline="No new leads waiting. Nice and clear." />
                    ) : (
                        <div className="grid gap-3">
                            {data.new_leads.map((l) => <LeadRow key={l.id} lead={l} />)}
                        </div>
                    )}
                </section>
                <section data-testid="today-follow-ups">
                    <SectionHead title="Waiting on follow-up" count={data.follow_ups.length} />
                    {data.follow_ups.length === 0 ? (
                        <EmptyState headline="Nobody waiting on a follow-up." />
                    ) : (
                        <div className="grid gap-3">
                            {data.follow_ups.map((l) => <LeadRow key={l.id} lead={l} showStatus />)}
                        </div>
                    )}
                </section>
            </div>

            <section data-testid="today-quick-actions">
                <SectionHead title="Quick actions" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {QUICK_ACTIONS.map((qa) => {
                        const Icon = qa.icon;
                        return (
                            <Link key={qa.to} to={qa.to} className="a1-card a1-card-link no-underline flex items-center gap-3 py-4" data-testid={qa.testId}>
                                <Icon size={18} className="text-[var(--color-accent)] shrink-0" />
                                <span className="font-semibold text-sm">{qa.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

function Stat({ to, label, value, warning, testId }) {
    return (
        <Link
            to={to}
            className={`a1-card today-stat no-underline ${warning ? "border-2 border-[var(--color-warning)]" : ""}`}
            data-testid={testId}
        >
            <span className="today-stat-value">{value}</span>
            <span className="today-stat-label">{label}</span>
        </Link>
    );
}

function SectionHead({ title, count }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold m-0" style={{ color: "var(--color-text)" }}>{title}</h2>
            {typeof count === "number" ? (
                <span
                    className="a1-badge text-xs"
                    style={{ background: "var(--color-bg-card)", color: "var(--color-text-muted)", border: "1px solid var(--color-line)" }}
                >
                    {count}
                </span>
            ) : null}
        </div>
    );
}

function LeadRow({ lead, showStatus }) {
    return (
        <div className="a1-card flex flex-col gap-2" data-testid={`today-lead-${lead.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`a1-badge ${URGENCY_COLOR[lead.urgency_tag] || "bg-[var(--color-line)]"}`} title={lead.urgency_tag}>
                        {URGENCY_LABEL[lead.urgency_tag] || lead.urgency_tag}
                    </span>
                    {showStatus ? (
                        <span className="a1-badge" style={{ background: "var(--color-bg-card)", color: "var(--color-text-muted)", border: "1px solid var(--color-line)" }}>
                            {lead.status}
                        </span>
                    ) : null}
                </div>
                <span className="text-xs text-[var(--color-text-muted)]">{timeAgo(lead.created_at)}</span>
            </div>
            <div>
                <p className="font-semibold m-0">{lead.name || "(no name)"} — {lead.city}</p>
                <p className="text-sm text-[var(--color-text-muted)] m-0">
                    {formatPhone(lead.phone)}{lead.service_needed ? ` · ${lead.service_needed}` : ""}
                </p>
            </div>
            <div className="flex gap-2">
                <a href={`tel:${lead.phone}`} className="a1-btn-secondary text-xs py-2 flex-1 justify-center">
                    <Phone size={13} /> Call
                </a>
                <a href={`sms:${lead.phone}`} className="a1-btn-secondary text-xs py-2 flex-1 justify-center">
                    <MessageSquare size={13} /> Text
                </a>
                <Link to={`/admin/leads/${lead.id}`} className="a1-btn-primary text-xs py-2 flex-1 justify-center">
                    Open <ArrowRight size={13} />
                </Link>
            </div>
        </div>
    );
}
